import { StepHelpers } from "@shared/components/Stepper";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  CheckCircle2,
  ChevronLeft,
  FileText,
  Users,
  Calendar,
  Clock,
  MessageSquare,
  AlertCircle,
  Search,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, PaginationInfo } from "@shared/types/table";
import { Input } from "@shared/ui/input";

interface ReviewSubmitStepProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep: (stepName: string) => void;
  stopTracking: () => void;
  stepHelpers?: StepHelpers;
  onPrevious?: () => void;
  onNext?: () => void;
}

export const ReviewSubmitStep: React.FC<ReviewSubmitStepProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stopTracking,
  stepHelpers,
  onPrevious,
  onNext,
}) => {
  // Retrieve data from previous steps
  const selectionData = useMemo(
    () => stepHelpers?.getStepData(0) || {},
    [stepHelpers],
  );
  const requestDetails = useMemo(
    () => stepHelpers?.getStepData(1) || {},
    [stepHelpers],
  );

  console.log("requestDetails", requestDetails);

  const selectedConsumers = selectionData.selectedConsumers || [];

  // Local state for search and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  // Local filtering logic
  const filteredConsumers = useMemo(() => {
    if (!searchTerm.trim()) return selectedConsumers;
    const term = searchTerm.toLowerCase();
    return selectedConsumers.filter((consumer: any) => {
      const name =
        `${consumer.firstName || ""} ${consumer.lastName || ""}`.toLowerCase();
      const nameAlt =
        `${consumer.first_name || ""} ${consumer.last_name || ""}`.toLowerCase();
      const consumerNo = (consumer.consumerNo || "").toLowerCase();
      return (
        name.includes(term) ||
        nameAlt.includes(term) ||
        consumerNo.includes(term)
      );
    });
  }, [selectedConsumers, searchTerm]);

  // Local pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredConsumers.slice(startIndex, startIndex + pageSize);
  }, [filteredConsumers, currentPage, pageSize]);

  const paginationInfo: PaginationInfo = {
    currentPage,
    totalPages: Math.ceil(filteredConsumers.length / pageSize),
    totalItems: filteredConsumers.length,
    itemsPerPage: pageSize,
    startItem: (currentPage - 1) * pageSize + 1,
    endItem: Math.min(currentPage * pageSize, filteredConsumers.length),
  };

  const columns: TableColumn<any>[] = [
    { header: "Account No", key: "consumerNo", width: "150px" },
    {
      header: "Name",
      key: "firstName",
      render: (value: string, row: any) => {
        const first = value || row?.first_name || "";
        const last = row?.lastName || row?.last_name || "";
        return `${first} ${last}`.trim() || "N/A";
      },
    },
    {
      header: "Premise",
      key: "premise",
      render: (value: any, row: any) =>
        row?.territoryData?.service?.premise || "N/A",
    },
  ];

  const handleFinalSubmit = () => {
    // This will trigger the handleNext in Stepper, which calls onComplete on the last step
    logTimeUntilStep("Review & Submit");
    onNext?.();
    stopTracking?.();
  };

  const InfoItem = ({
    icon: Icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: string;
  }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-neutral-100 bg-neutral-50/50">
      <div className="p-2 bg-white rounded-md border border-neutral-100 ring-1 ring-neutral-100 flex-shrink-0">
        <Icon className="h-4 w-4 text-blue-500" />
      </div>
      <div>
        <p className="text-xs font-medium text-neutral-500  tracking-wider">
          {label}
        </p>
        <p className="text-sm font-semibold text-neutral-900">
          {value || "Not specified"}
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Selection Summary */}
        <div className="lg:col-span-2 space-y-6">
          <div className="p-4 border rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <h3 className="text-lg font-semibold text-neutral-800">
                  Selected Consumers
                </h3>
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-700 ml-2"
                >
                  {selectedConsumers.length}
                </Badge>
              </div>
            </div>

            <div className="border border-neutral-100 rounded-xl overflow-hidden bg-white shadow-sm p-2">
              <GenericTable
                data={paginatedData}
                columns={columns}
                rowKey="id"
                getRowClassName={() => "text-xs"}
                onSearch={setSearchTerm}
                searchPlaceholder="Search consumers..."
                emptyMessage={
                  searchTerm
                    ? `No consumers found matching "${searchTerm}"`
                    : "No consumers selected. Please go back to step 1."
                }
                pagination={paginationInfo}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

          <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
            <div className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900">
                  Ready to Submit
                </h4>
                <p className="text-xs text-blue-700 mt-1">
                  Please review all information carefully. Once submitted, the
                  disconnection process will be initiated for all{" "}
                  {selectedConsumers.length} consumers.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Request Details Summary */}
        <div className="lg:col-span-1 space-y-6">
          <div className="p-4 border rounded-xl bg-white shadow-sm">
            <div className="flex items-center gap-2 mb-6 px-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-neutral-900">
                Request Details
              </h3>
            </div>

            <div className="space-y-4">
              <InfoItem
                icon={AlertCircle}
                label="Reason"
                value={requestDetails.reasonLabel || requestDetails.reason}
              />
              <InfoItem
                icon={Calendar}
                label="Scheduled Date"
                value={
                  requestDetails.scheduledDate
                    ? new Date(
                        requestDetails.scheduledDate,
                      ).toLocaleDateString()
                    : "Not specified"
                }
              />
              <InfoItem
                icon={Clock}
                label="Time Slot"
                value={requestDetails.timeSlotLabel || requestDetails.timeSlot}
              />
              <InfoItem
                icon={MessageSquare}
                label="Additional Notes"
                value={
                  requestDetails.consumerRemark ||
                  "No additional comments provided."
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-neutral-200 mt-8">
        <Button
          onClick={onPrevious}
          variant="ghost"
          className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900"
        >
          <ChevronLeft size={16} />
          <span>Back to Details</span>
        </Button>

        <Button onClick={handleFinalSubmit} variant="default">
          Submit Bulk Request
        </Button>
      </div>
    </div>
  );
};
